<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupQuestionsClosingCard, {QuestionsClosingAnswers} from "@/medication-checkup/components/MedicationCheckupQuestionsClosingCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {FollowUpClosingView} from "@/medication-checkup/interfaces/follow-up-closing";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useClosingDocument} from "@/medication-checkup/composables/useClosingDocument";
import {useKnowledgeAppraisal} from "@/medication-checkup/composables/useKnowledgeAppraisal";

/**
 * The scene closing the adherence follow-up: the end of the review of the
 * year.
 *
 * The words, the act to bill and — once validated — the questions, the
 * synthesis and the appraisal recorded and the two forms to download, the
 * follow-up and the conclusions of the year, are served by the handler.
 * Typing is the scene's own state: the closing action, always available
 * since nothing is required, submits it all. Validating closes the cycle: every scene then
 * reads frozen, and nothing is left to submit.
 */

const props = defineProps<SceneProps<FollowUpClosingView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, acted, act: submit} = useScene(props, {card: true});

const followUp = useClosingDocument(
    computed(() => view.value?.follow_up_recap ?? null),
    'suivi-observance',
    "Le suivi d'observance n'a pas pu être téléchargé."
);

const conclusions = useClosingDocument(
    computed(() => view.value?.conclusions_recap ?? null),
    'conclusions-bilan',
    "Les conclusions n'ont pas pu être téléchargées."
);

/**
 * The forms the closing offers, shown from the start and served once
 * validated, with the key the card hands back.
 */
const FORMS = [
    {key: 'follow_up_recap', label: "Télécharger le suivi d'observance (PDF)", form: followUp},
    {key: 'conclusions_recap', label: 'Télécharger les conclusions (PDF)', form: conclusions},
] as const;

const downloads = computed(() => FORMS
    .map(({key, label, form}) => ({key, label, available: !!view.value?.[key], downloading: form.downloading.value})));

const download = (key: string) => FORMS.find(candidate => candidate.key === key)?.form.download();

/**
 * The texts typed so far — local until the closing action submits them.
 */
const answers = ref<QuestionsClosingAnswers>({questions: '', conclusion: ''});

const {appraisal, submitted} = useKnowledgeAppraisal();

/**
 * Type into one field — local only: the closing action submits both.
 */
const typed = (field: keyof QuestionsClosingAnswers, value: string) => {
    answers.value = {...answers.value, [field]: value};
};

/**
 * Take the closing action, submitting the two texts and the appraisal.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    questions: answers.value.questions.trim() || null,
    conclusion: answers.value.conclusion.trim() || null,
    ...submitted()
});
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupQuestionsClosingCard
        v-if="view"
        headline="Votre suivi d'observance est terminé !"
        :recorded="view"
        :billing="view.billing"
        :downloads="downloads"
        frozen
        @download="download"
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupQuestionsClosingCard
        v-if="cardShown && view"
        headline="Votre suivi d'observance est terminé !"
        :recorded="view"
        :billing="view.billing"
        :downloads="downloads"
        :answers="answers"
        :appraisal="appraisal"
        class="animate-message-in"
        @typed="typed"
        @appraised="appraisal = $event"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        @act="act"
      />
    </template>
  </div>
</template>
